'use client'

import styles from './PerformanceHero.module.scss'

interface MoreArtistsLinkProps {
  /** `id` de la fila «Artistas» de la tabla de datos destacados. */
  readonly anchorId: string
  readonly hiddenCount: number
}

/**
 * «+X más» de la fila de artistas del hero.
 *
 * Salta con `scrollIntoView`, no con un ancla `href="#id"` nativa (mismo
 * criterio que `NoticeBanner`/`PerformanceSectionNav`): así el salto se puede
 * frenar en `prefers-reduced-motion` y no depende de que el navegador resuelva
 * el fragmento de la URL. El hueco del header sticky lo compensa el propio
 * destino con su `scroll-margin-top` (mixin `pdp-section-anchor` en
 * `PerformanceHighlights.module.scss`), no este componente.
 *
 * Es un `<button>`, no un `<a>` (regla de accesibilidad del repo: sin
 * navegación real no hay `href` que valga) — único motivo por el que esta
 * pieza necesita ser Client Component, separada del resto del hero.
 */
export function MoreArtistsLink({ anchorId, hiddenCount }: MoreArtistsLinkProps) {
  function handleClick() {
    const target = document.getElementById(anchorId)
    if (!target) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    })

    // `scrollIntoView` no mueve el foco (a diferencia de la navegación por
    // ancla nativa, que se lo da sola al destino cuando tiene `tabIndex`):
    // se fuerza aquí para no perder ese comportamiento (FR-057).
    // `preventScroll` evita un segundo salto redundante al enfocar.
    target.focus({ preventScroll: true })
  }

  return (
    <button type="button" onClick={handleClick} className={styles.moreArtists}>
      +{hiddenCount} más
    </button>
  )
}
